/**
 * STARSEED-CAPMARKETS-325 — wording for the per-account card.
 *
 * Pure, so every branch is testable without rendering. The API sends reason
 * CODES for any figure it could not give; this file is the one place they
 * become words, and `accountBreakdown.test.js` reads the Python module to
 * prove the two sets of codes are the same.
 *
 * Three rules shape everything below:
 *
 *   - Unknown is never zero (CAPMARKETS-181). A figure the API could not give
 *     arrives as `null` with a reason, and is shown as that reason. A total
 *     that covers only some accounts says which it leaves out.
 *   - A pooled account's $0.00 is "in the account", never a balance
 *     (CAPMARKETS-190): the money is in the pool, and the pool figure is always
 *     shown beside it. The words below avoid "balance" for that figure too.
 *   - No trade data (operator, 2026-09-21). No equity, no open trades, no
 *     profit or percentage computed between two figures.
 */
import { describeMoney } from "./partnerAccountQueue";
import { CONFIDENCE_DISCLOSURE, describeSource, formatAsOf } from "./heroBalance";

/** Every reason code `services/partner_account_breakdown.py` can send. */
export const NOTE_TEXT = {
  ledger_unavailable: "The partner's transaction record could not be read just now.",
  ledger_partial:
    "Some of the partner's recorded movements could not be matched to an account, so this is not shown until they are.",
  balance_unavailable: "The partner's live account figures could not be read just now.",
  not_reported: "The partner did not report a live figure for this account.",
  amounts_missing: "The partner left some amounts off this account's record.",
  paid_out_more_than_in:
    "More has been paid out of this account than was paid in, so its share of the pool cannot be worked out from its movements.",
  does_not_reconcile:
    "This account holds more than its recorded movements add up to, so its pool figure is not shown.",
  no_statement: "No statement has been recorded for this account yet.",
  statement_without_balance: "Its latest statement was recorded without a balance figure.",
};

/** Words for a reason code. An unknown code still says something true. */
export const describeNote = (code) =>
  (code && NOTE_TEXT[code]) || "Not available right now.";

/** A money figure, or null when it is not one. Never "$0.00" for null. */
export const moneyText = (value) => {
  const d = describeMoney(value);
  return d.known ? d.text : null;
};

/** The three flows shown on every row, in reading order. Direction comes from
 *  the sign of each movement, so capital that arrived as `transfer in` counts
 *  as paid in (CAPMARKETS-224). */
export const FLOW_LABELS = [
  ["paid_in", "Paid in"],
  ["paid_out", "Paid out"],
  ["adjustments", "Partner adjustments"],
];

export const movementParts = (movements) => {
  if (!movements) return [];
  return FLOW_LABELS.map(([key, label]) => ({
    key,
    label,
    text: moneyText(movements[key]) ?? "not fully reported",
  }));
};

/** "8 days old" style age, or null. */
export const ageText = (days) => {
  if (days === null || days === undefined || Number.isNaN(Number(days))) return null;
  const n = Number(days);
  if (n <= 0) return "today";
  return `${n} day${n === 1 ? "" : "s"} old`;
};

/** How the statement figure was obtained, in the dashboard's own words
 *  (CAPMARKETS-115: provenance travels with the number). */
export const provenanceText = (statement) => {
  if (!statement) return null;
  const bySource = describeSource(statement.source);
  if (bySource) return bySource.label;
  return CONFIDENCE_DISCLOSURE[statement.confidence]?.label || null;
};

/**
 * One line describing a statement figure, or null when there is none.
 *
 * A statement that exists without a balance says so; it is not "no
 * statement". Its date, age and provenance ride with it either way.
 */
export const statementText = (statement) => {
  if (!statement) return null;
  const date = formatAsOf(statement.as_of);
  const age = ageText(statement.age_days);
  const amount = moneyText(statement.balance);
  const head = amount
    ? [amount, date ? `on ${date}` : null].filter(Boolean).join(" ")
    : `No balance on the statement${date ? ` dated ${date}` : ""}`;
  return [head, provenanceText(statement), age].filter(Boolean).join(" · ");
};

/** The accounts a partial total leaves out, in words. */
export const excludedText = (total) => {
  if (!total || total.complete || !total.excluded || !total.excluded.length) return null;
  return total.excluded
    .map((e) => `${e.account}: ${describeNote(e.reason)}`)
    .join(" ");
};

/** "Covers 1 of 3 accounts." for a total that is not whole, else null. */
export const coverageText = (total) => {
  if (!total || total.complete || total.of === undefined) return null;
  return `Covers ${total.covers} of ${total.of} account${total.of === 1 ? "" : "s"}.`;
};
